'use client';

import { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

interface FormInputProps {
  type: 'text' | 'email' | 'password' | 'tel' | 'url';
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  label: string;
  icon?: React.ReactNode;
  error?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
}

export default function FormInput({
  type,
  id,
  value,
  onChange,
  placeholder,
  required = false,
  label,
  icon,
  error,
  onBlur,
  onFocus,
  className = ''
}: FormInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  // Validation logic
  useEffect(() => {
    if (value.length === 0) {
      setIsValid(null);
      return;
    }

    let valid = true;
    
    if (type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      valid = emailRegex.test(value);
    } else if (type === 'password') {
      // Supabase policy requires all four character classes — keep this list
      // in lockstep with /api/auth/signup or users get a green checklist
      // followed by a 400 from the API.
      const hasMinLength = value.length >= 10;
      const hasUppercase = /[A-Z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
      valid = hasMinLength && hasUppercase && hasNumber && hasSymbol;
    } else if (required) {
      valid = value.length > 0;
    }

    setIsValid(valid);
  }, [value, type, required]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const getInputType = () => {
    if (type === 'password') {
      return showPassword ? 'text' : 'password';
    }
    return type;
  };

  const getInputClasses = () => {
    let classes = 'form-input';
    
    if (isFocused) {
      classes += ' focus';
    }
    
    if (isValid === true) {
      classes += ' valid';
    } else if (isValid === false || error) {
      classes += ' error';
    }
    
    return `${classes} ${className}`;
  };

  const getValidationIcon = () => {
    if (isValid === true) {
      return <CheckCircle className="validation-icon valid w-5 h-5" />;
    } else if (isValid === false || error) {
      return <AlertCircle className="validation-icon error w-5 h-5" />;
    }
    return null;
  };

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-medium text-slate-200 tracking-wide">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5">
            {icon}
          </div>
        )}
        
        <input
          type={getInputType()}
          id={id}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`${getInputClasses()} ${icon ? 'pl-12' : ''} ${(isValid !== null || error) ? 'pr-12' : ''}`}
          placeholder={placeholder}
          required={required}
          aria-describedby={`${id}-error`}
          aria-invalid={isValid === false || !!error}
        />
        
        {/* Password toggle */}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-12 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
        
        {/* Validation icon */}
        {getValidationIcon()}
      </div>
      
      {/* Error message */}
      {error && (
        <p id={`${id}-error`} className="text-red-400 text-sm mt-1" role="alert">
          {error}
        </p>
      )}
      
      {/* Validation hint */}
      {type === 'password' && value.length > 0 && !isValid && (
        <div className="text-yellow-400 text-sm mt-1 space-y-1">
          <p className="font-medium">Password requirements:</p>
          <ul className="text-xs space-y-0.5 ml-2">
            <li className={value.length >= 10 ? 'text-green-400' : 'text-yellow-400'}>
              ✓ At least 10 characters {value.length >= 10 ? '(✓)' : `(${value.length}/10)`}
            </li>
            <li className={/[A-Z]/.test(value) ? 'text-green-400' : 'text-yellow-400'}>
              ✓ At least 1 uppercase letter {/[A-Z]/.test(value) ? '(✓)' : '(missing)'}
            </li>
            <li className={/\d/.test(value) ? 'text-green-400' : 'text-yellow-400'}>
              ✓ At least 1 number {/\d/.test(value) ? '(✓)' : '(missing)'}
            </li>
            <li className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ? 'text-green-400' : 'text-yellow-400'}>
              ✓ At least 1 symbol {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ? '(✓)' : '(missing)'}
            </li>
          </ul>
        </div>
      )}
    </div>
  );
} 